import React, { useState, useEffect } from 'react';
import axios from "axios";
import { User, Phone, Mail, Lock, Briefcase, Building, Save, X, CheckCircle, ArrowRight, ArrowLeft, UserPlus, ChevronRight } from 'lucide-react';

const inputStyle = (err) => ({
  width: '100%', padding: '11px 14px 11px 40px',
  background: err ? 'rgba(248,113,113,0.06)' : 'var(--nd-w04)',
  border: `1px solid ${err ? 'rgba(248,113,113,0.35)' : 'var(--nd-w08)'}`,
  borderRadius: '10px', color: 'var(--nd-text)', fontSize: '13px',
  fontFamily: "'DM Sans',sans-serif", outline: 'none',
  transition: 'border-color .2s, box-shadow .2s', colorScheme: 'dark',
});

const STEPS = [
  { number: 1, title: 'Personal Info', icon: User },
  { number: 2, title: 'Account Setup', icon: Lock },
  { number: 3, title: 'Role & Dept',   icon: Briefcase },
];

// --- Field Component moved OUTSIDE to prevent focus loss ---
const Field = ({ label, name, type='text', icon: Icon, placeholder, options, value, onChange, error }) => (
  <div>
    <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
      {label} <span style={{ color: '#F87171' }}>*</span>
    </div>
    <div style={{ position: 'relative' }}>
      <Icon size={14} color="var(--nd-tx22)" style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 2 }}/>
      {options ? (
        <select name={name} value={value} onChange={onChange}
          style={{ ...inputStyle(error), appearance: 'none', cursor: 'pointer' }}
          onFocus={e => { e.target.style.borderColor='rgba(34,211,165,0.38)'; e.target.style.boxShadow='0 0 0 3px rgba(34,211,165,0.07)'; }}
          onBlur={e => { e.target.style.borderColor=error?'rgba(248,113,113,0.35)':'var(--nd-w08)'; e.target.style.boxShadow='none'; }}>
          <option value="">Select {label}…</option>
          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          style={inputStyle(error)}
          onFocus={e => { e.target.style.borderColor='rgba(34,211,165,0.38)'; e.target.style.boxShadow='0 0 0 3px rgba(34,211,165,0.07)'; }}
          onBlur={e => { e.target.style.borderColor=error?'rgba(248,113,113,0.35)':'var(--nd-w08)'; e.target.style.boxShadow='none'; }}/>
      )}
    </div>
    {error && <div style={{ fontSize: '11px', color: '#F87171', fontWeight: '600', marginTop: '5px' }}>⚠ {error}</div>}
  </div>
);

const CreateUserStyled = () => {
  const [formData, setFormData] = useState({ Username:'',Firstname:'',Lastname:'',MobileNumber:'',Password:'',Type:'',Department:'',Email:'',client_id:'' });
  // Customer logins must be linked to a client (an EPC counts as a client) - loaded once a Customer role is picked.
  const [clientOptions, setClientOptions] = useState([]);
  useEffect(() => {
    if (formData.Type !== 'customer' || clientOptions.length > 0) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`)
      .then(r => {
        const list = r.data?.data?.clients || [];
        setClientOptions(list.map(c => ({ v: String(c.id), l: `${c.client_name}${c.is_epc ? ' (EPC)' : ''}` })));
      })
      .catch(() => {});
  }, [formData.Type, clientOptions.length]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const validateForm = () => {
    const e = {};
    if (currentStep===1) {
      if (!formData.Firstname.trim()) e.Firstname='First name is required';
      if (!formData.Lastname.trim()) e.Lastname='Last name is required';
      if (!formData.MobileNumber.trim()) e.MobileNumber='Mobile number is required';
      if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) e.Email='Invalid email format';
    }
    if (currentStep===2) {
      if (!formData.Username.trim()) e.Username='Username is required';
      if (!formData.Password) e.Password='Password is required';
      if (formData.Password.length<6) e.Password='Min 6 characters';
    }
    setErrors(e); return Object.keys(e).length===0;
  };

  const handleSubmit = async () => {
    const e = {};
    if (!formData.Type) e.Type='Role is required';
    if (!formData.Department) e.Department='Department is required';
    if (formData.Type==='customer' && !formData.client_id) e.client_id='Pick the client (or EPC) this login belongs to';
    if (Object.keys(e).length>0) { setErrors(e); return; }
    setIsSubmitting(true);
    try {
      const payload = { ...formData };
      if (payload.Type !== 'customer') delete payload.client_id;
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/create-user`, payload);
      alert('✅ User created successfully.');
      handleReset();
    } catch (err) {
      alert(`❌ ${err.response?.data?.message || err.response?.data?.error || 'Failed to create user.'}`);
    } finally { setIsSubmitting(false); }
  };

  const handleChange = (e) => {
    const {name,value} = e.target;
    setFormData(p=>({...p,[name]:value}));
    if (errors[name]) setErrors(p=>({...p,[name]:''}));
  };

  const handleReset = () => { setFormData({Username:'',Firstname:'',Lastname:'',MobileNumber:'',Password:'',Type:'',Department:'',Email:'',client_id:''}); setErrors({}); setCurrentStep(1); };

  return (
    <div style={{ padding: '14px 20px', fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes cu-spin{to{transform:rotate(360deg)}} select option{background:var(--nd-surface-2);color:var(--nd-text)}`}</style>

      {/* Header */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '20px 24px 18px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--nd-tx25)', marginBottom: '10px' }}>
          <span>Admin</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color: 'rgba(34,211,165,0.55)' }}>Create User</span>
        </div>
        <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)', letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          <UserPlus size={22} color="#3B82F6"/>Create New User
        </div>
        <div style={{ height: '2px', borderRadius: '2px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {/* Stepper */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '22px 32px', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '560px', margin: '0 auto' }}>
          {STEPS.map((step, idx) => {
            const done = currentStep > step.number, active = currentStep === step.number;
            const Icon = step.icon;
            return (
              <React.Fragment key={step.number}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: done||active ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'var(--nd-w06)',
                    border: `1px solid ${done||active ? 'transparent' : 'var(--nd-w10)'}`,
                    color: done||active ? 'var(--nd-text-strong)' : 'var(--nd-tx30)',
                    transform: active ? 'scale(1.08)' : 'scale(1)',
                    boxShadow: active ? '0 6px 20px rgba(34,211,165,0.25)' : 'none',
                    transition: 'all .3s',
                  }}>
                    {done ? <CheckCircle size={20}/> : <Icon size={18}/>}
                  </div>
                  <span style={{ marginTop: '7px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', color: done||active ? '#22D3A5' : 'var(--nd-tx25)', textTransform: 'uppercase' }}>{step.title}</span>
                </div>
                {idx < 2 && (
                  <div style={{ height: '3px', flex: 1, margin: '0 -8px', marginBottom: '24px', zIndex: 1, borderRadius: '2px', background: currentStep > step.number ? 'linear-gradient(90deg,#3B82F6,#22D3A5)' : 'var(--nd-w08)', transition: 'background .5s' }}/>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Form Card */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', overflow: 'hidden' }}>
        <div style={{ padding: '28px 32px' }}>

          {/* Step 1 */}
          {currentStep===1 && (<>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--nd-text)', marginBottom: '4px' }}>Personal Details</div>
            <div style={{ fontSize: '12px', color: 'var(--nd-tx30)', marginBottom: '24px' }}>Basic information about the employee</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '18px' }}>
              <Field label="First Name" name="Firstname" icon={User} placeholder="John" value={formData.Firstname} onChange={handleChange} error={errors.Firstname} />
              <Field label="Last Name" name="Lastname" icon={User} placeholder="Doe" value={formData.Lastname} onChange={handleChange} error={errors.Lastname} />
              <Field label="Mobile Number" name="MobileNumber" icon={Phone} placeholder="+92 300 0000000" value={formData.MobileNumber} onChange={handleChange} error={errors.MobileNumber} />
              <Field label="Email Address" name="Email" type="email" icon={Mail} placeholder="john@sowash.pk" value={formData.Email} onChange={handleChange} error={errors.Email} />
            </div>
          </>)}

          {/* Step 2 */}
          {currentStep===2 && (<>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--nd-text)', marginBottom: '4px' }}>Account Setup</div>
            <div style={{ fontSize: '12px', color: 'var(--nd-tx30)', marginBottom: '24px' }}>Login credentials for the system</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '18px' }}>
              <Field label="Username" name="Username" icon={User} placeholder="johndoe" value={formData.Username} onChange={handleChange} error={errors.Username} />
              <Field label="Password" name="Password" type="password" icon={Lock} placeholder="Min 6 characters" value={formData.Password} onChange={handleChange} error={errors.Password} />
            </div>
          </>)}

          {/* Step 3 */}
          {currentStep===3 && (<>
            <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--nd-text)', marginBottom: '4px' }}>Role & Department</div>
            <div style={{ fontSize: '12px', color: 'var(--nd-tx30)', marginBottom: '24px' }}>Assign access levels and department</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '18px' }}>
              <Field label="User Role" name="Type" icon={Briefcase} value={formData.Type} onChange={handleChange} error={errors.Type} options={[
                {v:'Operations',l:'Operations'},{v:'Sales',l:'Sales'},{v:'Admin',l:'Admin'},
                {v:'FO',l:'Field Operator'},{v:'ci_admin',l:'CI Admin'},{v:'customer',l:'Customer'},{v:'accounts',l:'Accounts'}
              ]}/>
              <Field label="Department" name="Department" icon={Building} value={formData.Department} onChange={handleChange} error={errors.Department} options={[
                {v:'Administration',l:'Administration'},{v:'Sales',l:'Sales'},{v:'Operations',l:'Operations'},
                {v:'C&I',l:'Commercial & Industrial'},{v:'accounts',l:'Accounts'}
              ]}/>
              {formData.Type==='customer' && (
                <Field label="Client / EPC account" name="client_id" icon={Building} value={formData.client_id} onChange={handleChange} error={errors.client_id}
                  options={clientOptions} />
              )}
            </div>
          </>)}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid var(--nd-w06)' }}>
            <div>
              {currentStep > 1 && (
                <button onClick={() => setCurrentStep(s=>s-1)} style={{ padding: '10px 20px', background: 'var(--nd-w05)', border: '1px solid var(--nd-w08)', borderRadius: '10px', color: 'var(--nd-tx45)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontFamily: "'DM Sans',sans-serif" }}>
                  <ArrowLeft size={15}/>Back
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {currentStep===3 && (
                <button onClick={handleReset} style={{ padding: '10px 18px', background: 'none', border: 'none', color: 'var(--nd-tx30)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'DM Sans',sans-serif" }}>
                  <X size={14}/>Clear
                </button>
              )}
              {currentStep < 3 ? (
                <button onClick={() => validateForm() && setCurrentStep(s=>s+1)} style={{ padding: '10px 22px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 14px rgba(34,211,165,0.18)' }}>
                  Next<ArrowRight size={15}/>
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={isSubmitting} style={{ padding: '10px 24px', background: isSubmitting ? 'var(--nd-w10)' : 'linear-gradient(90deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '13px', cursor: isSubmitting?'not-allowed':'pointer', display: 'flex', alignItems: 'center', gap: '7px', opacity: isSubmitting?0.6:1, fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 14px rgba(34,211,165,0.18)' }}>
                  {isSubmitting ? <><span style={{ width:'13px',height:'13px',border:'2px solid var(--nd-tx25)',borderTop:'2px solid white',borderRadius:'50%',animation:'cu-spin .7s linear infinite',display:'inline-block'}}/>Creating…</> : <><Save size={14}/>Create User</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUserStyled;